/**
 * users/{uid} doc helper + React hook.
 *
 * One Firestore doc per signed-in person, the source of truth for role
 * and approval status surfaced to the UI. The matching custom claim
 * (request.auth.token.role) is the security-rules gate; the doc mirrors
 * it for display + queries.
 *
 * Lifecycle:
 *   1. Firebase Auth sign-in fires -> AuthProvider sees `user`.
 *   2. useUserDoc subscribes to users/{user.uid}.
 *   3. If the doc doesn't exist yet, ensureUserDoc() writes it once.
 *      - Bobbie (DIRECTOR_BOOTSTRAP_EMAIL, verified) gets role='director',
 *        status='active' via the Director-by-email rule.
 *      - Everyone else gets role='family', status='pending': they sit on
 *        the "waiting for approval" card until Bobbie approves them.
 *   4. onSnapshot keeps the React state in sync with any future change
 *      (e.g. Bobbie approves a pending user -> their status flips live).
 *
 * The bootstrap email is duplicated here for client-side display logic
 * (so we can render "welcome, Director" optimistically before the write
 * round-trips), but the SECURITY decision is made by the Firestore rule.
 * Don't tighten one without tightening the other.
 */

import { useEffect, useMemo, useState } from 'react';
import { expandManagerRoleOptions, managerFlavorLabel, type ManagerTitle } from './managerTitle';
import {
  withBoardNotificationLevel,
  type NotifiableBoardKey,
  type BoardNotificationLevel,
  type BoardNotificationLevelOptions,
} from './boardNotifications';
import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { getAuth, type User } from 'firebase/auth';
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
} from 'firebase/storage';
import { db, storage } from './firebase';
import { uploadBytes, withUploadTimeout } from './storageUpload';
import {
  createUserDocTolerantly,
  ensureOnce,
} from './userDocBootstrap';
import { useAuth } from './auth-context';
import { useReadEpoch } from './readEpoch';
import { usePreviewRole, canPreviewRole, type PreviewRole } from './previewRole';
import { emailVerificationWaived } from './demoStudio';
import { functionHeaders } from './functionSecurity';
import { clearSentryUser, setSentryUser } from './sentry';
import { isJrStaffDancerPrincipal } from './jrStaffDancerSession';
import { isOwnJrStaffSignIn } from './jrStaffAliases.ts';

/**
 * Founding-Director email. MUST stay in sync with the
 * isDirectorByEmail() check in shared/firestore.rules.
 */
export const DIRECTOR_BOOTSTRAP_EMAIL = 'bobbiebford@gmail.com';

import type { UserRole } from './userRole.ts';
export type { UserRole } from './userRole.ts';
export type UserStatus = 'pending' | 'active' | 'rejected' | 'inactive' | 'dancer';

/**
 * Named studio groups are memberships, not permission-bearing roles. A person
 * can therefore be both Jr Staff and an Intern without either assignment
 * replacing the other. Keep this allow-list in sync with Firestore rules.
 */
export const STUDIO_GROUPS = ['interns'] as const;
export type StudioGroup = (typeof STUDIO_GROUPS)[number];
export const STUDIO_GROUP_LABELS: Record<StudioGroup, string> = {
  interns: 'Interns',
};

/**
 * Named audiences a DISCUSSION may be addressed to, which is a different list
 * from STUDIO_GROUPS above. 'jrStaff' is never a value in anybody's user doc:
 * its membership is the Jr Staff board's derived roll at
 * jrStaffBoardMembership/current, which is how an announcement addressed to
 * Jr Staff reaches the parents standing in for the Jr Staff who have no staff
 * account of their own (Bobbie, 2026-08-30: "Can we fix the announcements go
 * to the correct people when selecting Jr staff?"). Mirrored server-side by
 * AUDIENCE_GROUPS in hub/netlify/functions/lib/discussionWrite.mjs and by
 * jrStaffAudienceCoversRollCaller in shared/firestore.rules.
 */
export const JR_STAFF_AUDIENCE_GROUP = 'jrStaff';
export type DiscussionAudienceGroup = StudioGroup | typeof JR_STAFF_AUDIENCE_GROUP;

/**
 * Human-facing label for a role. Internal IDs stay as-is for
 * backward compatibility (rules, queries, doc fields), but everything
 * shown to a user goes through ROLE_LABELS so we can rename freely.
 *
 *   company  -> "Executive Teachers"  (senior teachers)
 *   ensemble -> "Jr Staff"            (junior teachers)
 *   foh      -> "Admin"           (front of house, office)
 */
export const ROLE_LABELS: Record<UserRole, string> = {
  director: 'Director',
  manager: 'Manager',
  company: 'Executive Teachers',
  ensemble: 'Jr Staff',
  foh: 'Admin',
  family: 'Family',
};

/**
 * Two manager names, same credentials, no bare Manager on the assignment
 * surfaces (Bobbie, 2026-08-14 evening, DECISIONS.md: "So two names same
 * credentials. And remove manger as a single title."). Both flavors are
 * role 'manager' with the identical union powers from #615 - this field is
 * DISPLAY ONLY, chosen so Kim can keep straight who is which flavor. Never
 * client-writable by the account itself; written only by the same
 * Director-only paths that write role (setRole, tempPassword create,
 * invite/acceptInvite). See shared/firestore.rules hasUserShape.
 *
 * The actual constants and pure logic live in managerTitle.ts, which is
 * import-free so `npm run test:handlers` can exercise it directly (see that
 * file's own comment and hub/tests/manager-two-names.test.mjs).
 */
export {
  MANAGER_TITLES,
  MANAGER_TITLE_LABELS,
  isManagerTitle,
  type ManagerTitle,
} from './managerTitle';

/**
 * The label to show for this person's role wherever it renders: pills,
 * cards, lists, greetings. A manager with a stored flavor shows the flavor
 * name; every other role, and a manager with no flavor yet (existing plain
 * accounts, per the ruling above), falls back to the plain ROLE_LABELS name.
 * Nobody's powers differ by flavor - no capability check may ever read
 * managerTitle, only this display path.
 */
export function roleLabelFor(
  person: Pick<UserDoc, 'role'> & Partial<Pick<UserDoc, 'managerTitle'>>
): string {
  return (
    managerFlavorLabel(person.role, person.managerTitle)
    ?? ROLE_LABELS[person.role]
    ?? person.role
  );
}

/**
 * Options for an assignment surface (invite, approve-as, role change): every
 * built-in role, with 'manager' expanded into its two named flavors so
 * nobody can pick a bare Manager. Both flavors write role='manager' with
 * identical credentials; only the stored name differs.
 */
export interface AssignableRoleOption {
  /** Unique <option value>. "manager:admin" / "manager:company" for the two flavors. */
  value: string;
  label: string;
  role: UserRole;
  managerTitle: ManagerTitle | null;
}
export function assignableRoleOptions(
  roles: readonly UserRole[]
): AssignableRoleOption[] {
  return expandManagerRoleOptions(roles, ROLE_LABELS, 'manager');
}

/** Just the staff roles, in display order. */
export const STAFF_ROLES: UserRole[] = [
  'director',
  'manager',
  'company',
  'ensemble',
  'foh',
];

/**
 * WHICH ACCOUNTS ARE THE SAME HUMAN lives in ./jrStaffAliases.ts
 * (isOwnJrStaffSignIn, jrStaffAliasUidsFor), imported FROM THERE by every
 * caller rather than re-exported through this file. This branch briefly
 * carried its own ./identityAliases.ts for the same job; #1046 landed the
 * GUARDED version - a guardian's account carries her dancer's studentId, so
 * matching on studentId alone answers "my mother is me" - and the duplicate
 * was deleted in favour of it. Not re-exported here on purpose: several
 * tests transpile this file and hand it a hand-written stub table
 * (tests/jr-staff-login-gate.test.mjs is one), so a convenience re-export
 * breaks their load. ONE THING, ONE PLACE: do not add a copy here.
 */

export function isStaffRole(role: UserRole | null | undefined): boolean {
  return !!role && STAFF_ROLES.includes(role);
}

export interface NotifPrefs {
  /** In-app push notification (when FCM ships). */
  app: boolean;
  /** Email digest / direct emails. */
  email: boolean;
  /** SMS (Twilio). Off by default since it costs money per send. */
  sms: boolean;
  /**
   * Category switches (Bobbie, 2026-08-07: "can they click what
   * notifications they receive?"). Absent means ON - the notify function
   * treats only an explicit false as off, so every account that saved
   * prefs before these keys existed keeps hearing everything.
   */
  messages?: boolean;
  calendar?: boolean;
  comp?: boolean;
  /**
   * One evening summary in addition to immediate alerts. Absent means OFF.
   */
  digest?: boolean;
  /**
   * The three-level board notifications button (Bobbie, 2026-08-30,
   * verbatim: "Notifications for everything / Notifications just for
   * original post / No notifications for anything in this board" - task
   * #31). Holds what is OFF, never what is on: absent or empty means
   * everything, '<boardKey>' alone means nothing (this is the old Leave
   * board button - access stays, only the pings stop), '<boardKey>:replies'
   * means just original posts. boardKey is the exact string the notify
   * path's own authorityLane() returns for that board
   * (notify.mjs/lib/notificationAuthority.mjs) - 'compBoard', 'jrStaffBoard'
   * or 'parentBoard' today. Capped at 200 by the rules
   * (shared/firestore.rules' hasNotifPrefsShape); see
   * lib/boardNotifications.ts for the level<->entry conversion.
   */
  mutedIds?: string[];
}

/**
 * Prefs with every optional key made concrete, for toggle UIs - a checkbox
 * bound to an absent key would render OFF while the reader treats absent as
 * ON. Categories default on, the additive digest defaults off, channels come from
 * DEFAULT_NOTIF_PREFS unless already saved.
 */
export function normalizedNotifPrefs(stored: NotifPrefs | undefined): Required<NotifPrefs> {
  // Explicit key by key, no spread-and-cast: a cast would hide any future
  // NotifPrefs key missing from this list, which is the exact compiler
  // check this function exists to provide (review P3, 2026-08-07).
  return {
    app: stored?.app ?? DEFAULT_NOTIF_PREFS.app,
    email: stored?.email ?? DEFAULT_NOTIF_PREFS.email,
    sms: stored?.sms ?? DEFAULT_NOTIF_PREFS.sms,
    messages: stored?.messages ?? true,
    calendar: stored?.calendar ?? true,
    comp: stored?.comp ?? true,
    digest: stored?.digest ?? false,
    // The per-board setting (task #31), untouched by this generic-channel
    // normalizer - Account/Landing's checkbox rows never read this key
    // (see their own Exclude<keyof NotifPrefs, 'mutedIds'> row types), and
    // BoardNotificationsButton reads the raw stored value directly, never
    // through this function.
    mutedIds: stored?.mutedIds ?? [],
  };
}

export const DEFAULT_NOTIF_PREFS: NotifPrefs = {
  app: true,
  email: true,
  sms: false,
};

export interface ClaimedDancer {
  firstName: string;
  lastName: string;
  dob: string;
}

export interface UserDoc {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  /** The person's own avatar color, chosen on Account. */
  profileColor?: ProfileColorName | null;
  role: UserRole;
  /**
   * Which named flavor of Manager this account shows as ("Manager/Admin" or
   * "Manager/Executive Teacher"). Only meaningful when role is 'manager';
   * display only, never a capability. See ManagerTitle above.
   */
  managerTitle?: ManagerTitle | null;
  /** Director-created overlay. The built-in `role` remains the security ceiling. */
  customRoleId?: string | null;
  customRoleName?: string | null;
  customRoleVersion?: number | null;
  /** Server-owned role-derived messaging lanes; never a client access grant. */
  customMessageGroups?: string[];
  status: UserStatus;
  /** Director-managed memberships that sit beside, never replace, role. */
  groups?: StudioGroup[];
  /** Director-approved label copied onto private Intern conversations. */
  internName?: string | null;
  /**
   * Optional studio start date (ISO YYYY-MM-DD). Drives the "X years at
   * the studio" counter on staff cards. Director sets it via the Manage
   * Users page; null/missing renders as "Unknown."
   */
  joinedStudioOn?: string | null;
  /**
   * The title an active teacher chose for every teacher-facing label.
   * Stored as a small stable value; UI copy formats it as "Ms. Firstname"
   * or "Coach Firstname" through lib/teacherTitle.ts.
   */
  teacherTitle?: 'ms' | 'coach';
  /**
   * How the user wants to hear from us. Asked once on first sign-in
   * via the Landing onboarding card; user can change anytime later via
   * a settings flow. Absence means "not yet asked": Landing shows the
   * onboarding card until set.
   */
  notifPrefs?: NotifPrefs;
  /**
   * Mobile number in E.164 (e.g. +15015550123) for SMS notifications. Only
   * used when notifPrefs.sms is on. Self-editable (non-privileged field).
   */
  phone?: string | null;
  /**
   * FCM registration tokens for this user's devices (web push). Added by
   * enablePush() per device; the notify function pushes to these when
   * notifPrefs.app is on. Self-writable (non-privileged).
   */
  fcmTokens?: string[];
  /** Parent-supplied hint only. Access still comes from students.familyUids. */
  claimedDancer?: ClaimedDancer;
  /** True if this doc was self-created via the Director-by-email bootstrap. */
  bootstrappedDirector?: boolean;
  /**
   * The dancer this login represents. Server-written only through the
   * setDancerLink Function; clients may read it but can never set or repoint it.
   */
  studentId?: string | null;
  /** Server-only concurrency token for dancer-link compensation. */
  studentLinkVersion?: string | null;
  /** Dedicated parent-managed dancer session marker. Never client-written. */
  authMode?: string | null;
  /**
   * JR STAFF PARITY: this dancer sign-in has been promoted to staff, so its
   * role/status pair reads {ensemble, active} exactly like an adult Jr Staff
   * account (netlify/functions/lib/jrStaffDancerBridge.mjs). Server-written
   * only, and frozen against the client update lanes in
   * shared/firestore.rules the same way authMode and studentId are.
   *
   * READ IT WHEREVER ROLE AND STATUS USED TO ANSWER "is this a dancer
   * login". After promotion they no longer do, on purpose - that identical
   * shape IS the parity - so this marker is the only thing left that tells
   * a promoted dancer sign-in apart from the adult account it now resembles.
   */
  jrStaffPromoted?: boolean;
  /** Persistent server-only marker for a retired Firebase-backed dancer. */
  retiredFirebaseDancer?: boolean;
  /**
   * True while this account is still on the studio temp password (set by the
   * tempPassword function's create/reset modes, cleared by its
   * password-changed mode after the forced first-login change). Written ONLY
   * server-side - it is not in the client-writable users update shape.
   */
  mustChangePassword?: boolean;
  createdAt: Timestamp | FieldValue;
  updatedAt: Timestamp | FieldValue;
}

export type ProfileColorName =
  | 'rose'
  | 'tangerine'
  | 'lime'
  | 'sky'
  | 'lavender'
  | 'mint'
  | 'coral'
  | 'honey';

export const PROFILE_COLOR_NAMES: readonly ProfileColorName[] = [
  'rose',
  'tangerine',
  'lime',
  'sky',
  'lavender',
  'mint',
  'coral',
  'honey',
];

export function isProfileColorName(value: unknown): value is ProfileColorName {
  return typeof value === 'string' && PROFILE_COLOR_NAMES.includes(value as ProfileColorName);
}

// Is this account eligible for the PARENT BOARD (the Comp Parent Board), the
// way the rules decide it? A studentId shuts out a dancer login, never a staff
// role (B13). It lives in a pure module so a node test can run it; see its
// docblock for the whole reasoning.
export { isParentBoardEligibleUserDoc } from './parentBoardEligibility.mjs';

/**
 * A child-only dancer sign-in. A real staff role wins because Jr Staff can
 * also be dancers; any missing/malformed role stays on the fail-closed child
 * surface instead of exposing adult routes.
 */
export function isDancerLoginUserDoc(
  user: Pick<UserDoc, 'role' | 'studentId'> | null | undefined
): boolean {
  return typeof user?.studentId === 'string'
    && user.studentId.trim().length > 0
    && !['director', 'manager', 'company', 'ensemble', 'foh'].includes(user.role);
}

/**
 * A real guardian account, safe to message as "the family": active, an
 * actual family-role account, and not a dancer's own sign-in (promoted or
 * not - see isDancerLoginUserDoc above).
 *
 * WHY role === 'family' IS NOT REDUNDANT WITH isDancerLoginUserDoc. That
 * check alone only catches an UNPROMOTED dancer login, because it fails
 * the moment role becomes a staff role - which is exactly what promotion
 * does (setStudentJrStaff.mjs flips role to 'ensemble'). Requiring
 * role === 'family' here closes the other half: a PROMOTED dancer login
 * (a real Jr Staff account, active, role 'ensemble') is excluded by the
 * role check alone, isDancerLoginUserDoc or not. Both conditions stay
 * because they catch two different-shaped accounts, not the same one
 * twice.
 */
export function isMessageableFamilyAccountDoc(
  user: Pick<UserDoc, 'role' | 'status' | 'studentId'> | null | undefined
): boolean {
  return !!user
    && user.status === 'active'
    && user.role === 'family'
    && !isDancerLoginUserDoc(user);
}

/**
 * THE FIX for CompBoard's Team tab "Message family" (PR #911 round 2,
 * Fable DO NOT SHIP, P1). A student's raw familyUids can hold the
 * dancer's OWN login uid, promoted or not (setDancerLink.mjs:244-249,
 * setStudentJrStaff.mjs:730, dancerClaims.mjs:475/725), and a deactivated
 * parent. Passed straight to createGroupThread/findOrCreateDm, those
 * produced three distinct failures the reviewer proved by reading the
 * server: an unpromoted dancer login makes threadGroupCreate.mjs refuse
 * the whole group with participant_invalid (the button fails for every
 * Jr Staff family); a promoted one is accepted and puts the minor
 * straight into "her family" group as a full participant; and a lone
 * dancer-login or inactive uid opens a DM nobody can ever read. This
 * takes the live users docs for those uids (the caller fetches them,
 * never this function - kept pure and testable) and keeps only real,
 * active, reachable guardians.
 *
 * usersByUid MAY MAP A UID TO undefined - and must, for two different
 * reasons the caller cannot always tell apart (round 3, DO NOT SHIP
 * again): a doc that genuinely does not exist, or a doc shared/
 * firestore.rules' users/{uid} read rule denied the caller (manager/
 * company/foh only get an active family doc; an inactive one, same as a
 * missing one, is refused outright). Both mean the same thing here - not
 * messageable - so a caller that fetches these docs must catch each read
 * individually and map a denial to undefined exactly like a miss, never
 * let one denied read in a batch fail every other read alongside it.
 */
export function resolveMessageableFamilyUids(
  familyUids: readonly string[],
  usersByUid: ReadonlyMap<string, Pick<UserDoc, 'role' | 'status' | 'studentId'> | null | undefined>
): string[] {
  return familyUids.filter((uid) => isMessageableFamilyAccountDoc(usersByUid.get(uid)));
}

/**
 * THE PER-READ CATCH (PR #911 round 3, Fable DO NOT SHIP again, P1).
 * openMessageFamily used to await a single Promise.all over every
 * familyUid's users doc read with no catch of its own - and
 * shared/firestore.rules:1512-1521's users/{uid} read rule denies
 * manager/company/foh a doc whose status is not 'active' (a missing doc
 * denies the same way; only the Director short-circuits past both). One
 * inactive or deleted parent anywhere in familyUids rejected the WHOLE
 * Promise.all, so the row printed the raw denial, "Missing or
 * insufficient permissions.", instead of ever reaching
 * resolveMessageableFamilyUids. `readUserDoc` is called once per uid,
 * each call's rejection caught independently and mapped to `undefined` -
 * which resolveMessageableFamilyUids already treats as not-messageable,
 * the same as a genuine miss (see its own doc comment) - so ONE denied
 * or deleted account can never take the rest of the batch down with it.
 * `readUserDoc` is injected so this stays testable with a fake that
 * rejects for one uid, without a live Firestore connection.
 */
export async function fetchFamilyUsersByUid(
  familyUids: readonly string[],
  readUserDoc: (uid: string) => Promise<Pick<UserDoc, 'role' | 'status' | 'studentId' | 'displayName'> | undefined>
): Promise<Map<string, Pick<UserDoc, 'role' | 'status' | 'studentId' | 'displayName'> | undefined>> {
  const entries = await Promise.all(
    familyUids.map(async (uid): Promise<[string, Pick<UserDoc, 'role' | 'status' | 'studentId' | 'displayName'> | undefined]> => {
      const data = await readUserDoc(uid).catch(() => undefined);
      return [uid, data];
    })
  );
  return new Map(entries);
}

/**
 * WHAT TO DO WITH THE RESOLVED GUARDIANS (PR #911 round 2, P2; revised
 * round 3, Fable DO NOT SHIP again). One guardian: the same
 * findOrCreateDm dedup every other DM in the Hub already gets. Zero
 * guardians: nothing to open, an honest empty result, never a guess.
 *
 * TWO OR MORE IS 'choose', NOT A SINGLE ACTION. Round 2 routed this case
 * to CommandCenter's class-board composer instead of useCreateGroupThread
 * (still right - that hook is deliberately create-only, see its own doc
 * comment in lib/threads.ts). But CommandCenter's own door,
 * canOpenCommandCenter, is "Kim's" - it dead-ends any teacher, Admin, or
 * uninvited Manager on a lock screen, so a caller who can see this button
 * at all (any staff role except ensemble, see canMessageFamily's own note
 * in CompBoard.tsx) could still be refused the composer it just sent them
 * to. 'choose' hands back every resolved uid so the caller can render one
 * "Message <first name>" button per guardian instead - each one a plain
 * findOrCreateDm DM, exactly the one-guardian case repeated once per
 * uid, never a thread that groups them together.
 */
export type MessageFamilyAction =
  | { kind: 'none' }
  | { kind: 'dm'; uid: string }
  | { kind: 'choose'; uids: string[] };

export function decideMessageFamilyAction(validUids: readonly string[]): MessageFamilyAction {
  if (validUids.length === 0) return { kind: 'none' };
  if (validUids.length === 1) return { kind: 'dm', uid: validUids[0] };
  return { kind: 'choose', uids: [...validUids] };
}

/**
 * Known child-only identities that must stay out of generic Director account
 * controls. The server repeats the stronger check with the account mapping
 * and legacy Auth claim; this client helper only controls presentation.
 */
export function isManagedDancerIdentityForAdminUi(
  user: (Pick<UserDoc, 'role' | 'status' | 'studentId'> &
    Partial<Pick<UserDoc, 'authMode' | 'retiredFirebaseDancer'>>) | null | undefined
): boolean {
  return user?.status === 'dancer'
    || user?.authMode === 'server_session_v1'
    || user?.retiredFirebaseDancer === true
    || isDancerLoginUserDoc(user);
}

/**
 * Explains why `userDoc` is null when loading is false and error is null -
 * so a caller cannot mistake "we deliberately never tried" for "everything
 * loaded fine, there is just no doc." Today the only such reason is an
 * unverified email (see the early return in useUserDoc below): we do not
 * create the doc until the address is proven, on purpose, so its absence
 * there is not a failure. Null means either there is a real error (check
 * `error`) or there simply is no signed-in user to explain.
 */
export type UserDocUnavailableReason = 'unverified-email';

function isBootstrapDirector(user: User): boolean {
  return (
    !!user.email &&
    user.email.toLowerCase() === DIRECTOR_BOOTSTRAP_EMAIL &&
    user.emailVerified
  );
}

/**
 * Create the user's Firestore doc on first sign-in. No-op if it already
 * exists (the snapshot listener will pick it up). Returns void; the
 * caller listens via onSnapshot for the actual state.
 *
 * Uses setDoc, NOT update: this is the only path that writes the doc
 * for the first time, and Firestore rules require the create shape to
 * be exactly what we send below.
 */
async function ensureUserDoc(user: User): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const ref = doc(db, 'users', user.uid);
  const bootstrap = isBootstrapDirector(user);
  const payload: UserDoc = {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    // NEVER user.photoURL: that is the Auth provider's photo (a Google
    // account photo, usually), and copying it here without anyone choosing
    // to share it is the root cause of the 2026-08-16 permission-denied bug
    // and the unconsented-photo display bug fixed in the same PR as this
    // comment (see TASK-QUEUE.md's P1 packet). A photo only becomes "their
    // Hub photo" through an explicit save to userAppearances.
    photoURL: null,
    role: bootstrap ? 'director' : 'family',
    status: bootstrap ? 'active' : 'pending',
    ...(bootstrap ? { bootstrappedDirector: true } : {}),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  // setDoc without merge would clobber an existing doc; we never want to
  // overwrite role/status on a re-run. The listener-then-create pattern
  // below means we only reach here if the doc is missing, but belt + braces:
  // guard with a snapshot check via the caller.
  await setDoc(ref, payload);
}

/**
 * SINGLETON CREATE. useUserDoc() mounts many times at once (Landing, nav,
 * chrome, assistant, feedback...), and each instance's listener sees the
 * same missing users/{uid} snapshot on a brand-new account. Before this
 * map every instance issued its own setDoc: the first write won as a
 * create, the rest were evaluated as updates changing createdAt and were
 * CORRECTLY denied by rules - and the denied losers showed a fatal
 * "Could not load your account" card over a doc that exists.
 *
 * Module scope (not hook scope) is the point: one ensure-create per uid
 * per session, no matter how many hook instances mount, all awaiting the
 * same promise. A successful entry is kept forever, so a later
 * exists=false snapshot from an empty offline cache can never replay the
 * create against the existing doc either. A failed entry is evicted by
 * ensureOnce() so a genuinely fresh attempt can retry, sequentially.
 */
const pendingUserDocCreates = new Map<string, Promise<void>>();

function ensureUserDocOnce(user: User): Promise<void> {
  return ensureOnce(pendingUserDocCreates, user.uid, () =>
    createUserDocTolerantly({
      create: () => ensureUserDoc(user),
      docExists: async () => {
        if (!db) return false;
        const snap = await getDoc(doc(db, 'users', user.uid));
        return snap.exists();
      },
    })
  );
}

export interface UseUserDocResult {
  /** True until the first snapshot (and any required ensure-write) lands. */
  loading: boolean;
  /**
   * The user doc. When an active Director is in "view as" preview, its
   * `role` is swapped to the previewed role so the whole app renders as
   * that role. Everything else on the doc (uid, status, ...) is unchanged.
   */
  userDoc: UserDoc | null;
  /** The real users/{uid} doc with the true role, ignoring any preview. */
  realUserDoc: UserDoc | null;
  /** The real role, or null. Use this for privileged writes / server calls. */
  realRole: UserRole | null;
  /** The active preview role, or null when not previewing. */
  previewRole: PreviewRole | null;
  /** Surface any error from the snapshot listener or the ensure write. */
  error: string | null;
  /**
   * Why there is no doc, when there is no doc and no error either. See
   * `UserDocUnavailableReason`. Null in the common cases: signed out,
   * still loading, or a doc that loaded fine.
   */
  reason: UserDocUnavailableReason | null;
}

/**
 * Subscribe to the current user's users/{uid} doc. Auto-creates the doc
 * on first sign-in via the Director-bootstrap or pending-family path.
 *
 * Designed so consumers can branch cleanly:
 *   - loading=true -> show a spinner / skeleton
 *   - userDoc=null + !loading + signed in + reason='unverified-email' ->
 *     expected, not a failure; point at the verify-email flow
 *   - userDoc=null + !loading + signed in + reason=null -> unexpected (we
 *     tried to create the doc and it failed; check error)
 *   - userDoc.status='pending' -> "waiting for Director to approve" card
 *   - userDoc.status='active' + role='director' -> Director home
 *   - userDoc.status='active' + role!='director' -> family/staff home
 */
export function useUserDoc(): UseUserDocResult {
  const { user, loading: authLoading } = useAuth();
  const [userDoc, setUserDoc] = useState<UserDoc | null>(null);
  const [docUser, setDocUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState<UserDocUnavailableReason | null>(null);
  // Only an unverified account needs the dancer-claim email exception.
  // Verified accounts can read their own document immediately, including
  // from Firestore's local cache, without another ID-token promise first.
  // This does not grant a role: the document and server rules still do that.
  const needsDancerClaim = !!user && !user.emailVerified && !emailVerificationWaived();
  const [dancerClaim, setDancerClaim] = useState<{ user: User; value: boolean } | null>(null);
  // Bind the answer to the exact Auth user. A previous person's true claim
  // must never waive email verification while the new person's is pending.
  const jrStaffDancer = !needsDancerClaim ? false
    : dancerClaim?.user === user ? dancerClaim.value : null;

  useEffect(() => {
    if (authLoading) return;
    if (!user || !needsDancerClaim) return;
    let current = true;
    void isJrStaffDancerPrincipal(user).then((value) => {
      if (current) setDancerClaim({ user, value });
    });
    return () => {
      current = false;
    };
  }, [authLoading, user, needsDancerClaim]);

  useEffect(() => {
    // Wait for auth to settle before we decide what to do.
    if (authLoading) return;
    setDocUser(user);
    setUserDoc(null);
    setLoading(!!user);
    setError(null);
    setReason(null);
    // Only an unverified account without the existing host waiver waits for
    // this claim. Unknown is never false for that account: a promoted dancer
    // has no inbox to verify and must retain the server-minted exception.
    if (jrStaffDancer === null) return;
    if (!user || !db) {
      setUserDoc(null);
      setLoading(false);
      setError(null);
      setReason(null);
      return;
    }
    // If the user hasn't verified their email yet, do NOT try to create
    // the doc: the Director-by-email rule requires email_verified=true,
    // and pending users shouldn't accumulate either until they've proven
    // they own the inbox. The verify card sits in front of all this.
    // TEMPORARY testing waiver (see emailVerificationWaived): on approved
    // testing hosts unverified sessions proceed to the ordinary pending-family
    // path, whose create rule does not require a verified email. The
    // Director bootstrap stays verified-only in both client and rules.
    // A PROMOTED JR STAFF DANCER HAS NO INBOX TO VERIFY (parity ruling, see
    // lib/jrStaffDancerSession.ts). Their principal comes from a Firebase
    // custom token minted by the dancer endpoint after the dancer password
    // was proven through its own rate-limited lane, so there is no email
    // address in play at all and emailVerified is false forever. Without
    // this branch the verify card would sit in front of every Jr Staff
    // dancer permanently and the parity would be dead on arrival.
    //
    // This is NOT a widening of the gate. The gate exists so an unproven
    // address cannot mint a users doc or reach the Director-by-email
    // bootstrap; a promoted dancer's users doc already exists, was written
    // by the server, and carries no address a human could claim. The claim
    // is read from the ID token, which no client can write, and it is
    // resolved BEFORE any branch is taken so a slow token read cannot let
    // the ordinary path run first.
    if (!user.emailVerified && !emailVerificationWaived() && !jrStaffDancer) {
      setUserDoc(null);
      setLoading(false);
      setError(null);
      // This is the one deliberate gap: say so, so a consumer like the nav
      // drawer can tell "waiting on a verified email" apart from "loaded
      // fine, nothing here" and "something actually broke" - both of which
      // also leave userDoc null.
      setReason('unverified-email');
      return;
    }

    setLoading(true);
    setError(null);
    // Clear any reason left over from a previous branch (e.g. a user who
    // just verified their email during this same mount) now that we're
    // proceeding to a real attempt.
    setReason(null);
    let attemptedCreate = false;
    let deliveredDoc = false;
    let current = true;

    const ref = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(
      ref,
      async (snap) => {
        if (!current) return;
        if (snap.exists()) {
          deliveredDoc = true;
          setUserDoc(snap.data() as UserDoc);
          // A delivered doc always outranks an old error. Without this a
          // hook instance that lost the bootstrap-create race kept its
          // stale permission-denied error forever, even after the winning
          // instance's doc arrived on this very listener.
          setError(null);
          setLoading(false);
          return;
        }
        // First sign-in: doc doesn't exist yet. Create it exactly once
        // PER SESSION (module-level singleton), not once per hook
        // instance. The snapshot re-fires with the new data after.
        if (attemptedCreate) {
          setLoading(false);
          return;
        }
        attemptedCreate = true;
        try {
          await ensureUserDocOnce(user);
          // snapshot listener will deliver the new data on its own.
        } catch (e) {
          if (!current) return;
          // ensureUserDocOnce already treats a permission-denied create as
          // benign when the doc exists, so anything landing here is a real
          // failure with the doc genuinely absent.
          setError(e instanceof Error ? e.message : String(e));
          setLoading(false);
        }
      },
      (err) => {
        if (!current) return;
        // The listener is dead after this callback. If we already have the
        // doc, keep showing it instead of stranding the user behind an
        // error card whose only exit is Sign out (which wipes IndexedDB
        // persistence and makes the next attempt start cold).
        if (deliveredDoc) {
          console.error('users/{uid} listener error (keeping last doc):', err);
          setLoading(false);
          return;
        }
        setError(err.message);
        setLoading(false);
      }
    );

    // Stall escape (stability audit F2): the SDK retries a dead stream
    // silently forever, so on a hostile network the snapshot above may
    // simply never arrive - no data, no error, loading=true for minutes.
    // That was Kim's "Setting up your account" hang. After 10 seconds with
    // nothing delivered, fetch the doc once, straight from the server; a
    // success unblocks the whole app, a failure surfaces a REAL error the
    // account cards can act on instead of endless silence.
    const stallTimer = window.setTimeout(() => {
      if (!current || deliveredDoc) return;
      getDocFromServer(ref)
        .then(async (snap) => {
          if (!current || deliveredDoc) return;
          if (snap.exists()) {
            deliveredDoc = true;
            setUserDoc(snap.data() as UserDoc);
            setError(null);
            setLoading(false);
            return;
          }
          // THE OTHER HALF OF THE SAME QUESTION (first opening audit, I-1).
          // A server read that succeeds and reports the doc ABSENT used to
          // set nothing, clear nothing and create nothing, because the
          // bootstrap create only ever fired from the snapshot callback
          // above, which on this network never runs. That left a brand new
          // family's FIRST sign-in on "Checking your account..." forever,
          // with no error and no create ever attempted, on exactly the
          // network this escape exists for (REST works, the listen channel
          // is blocked). The server has now answered, so answer it: take
          // the same branch the snapshot handler takes.
          //
          // ensureUserDocOnce is THE one create, deliberately: the rules
          // pin the create shape and never let a client choose a role, so
          // this must never grow a second write shape of its own. It is
          // also the same per uid singleton, so this cannot double-create
          // against the snapshot path if that path ever wakes up.
          if (attemptedCreate) {
            setLoading(false);
            return;
          }
          attemptedCreate = true;
          // Bounded by the same 10 seconds as the read that got us here. A
          // create that never acknowledges is the same dead end as a read
          // that never answers, and the honest "Try again" screen the
          // dancer cookie check already lands on is a better answer than a
          // spinner that never stops.
          let createDeadline: number | undefined;
          try {
            await Promise.race([
              ensureUserDocOnce(user),
              new Promise<never>((_resolve, reject) => {
                createDeadline = window.setTimeout(() => {
                  reject(new Error(
                    'Setting up your account took too long on this network.'
                  ));
                }, 10000);
              }),
            ]);
            if (!current || deliveredDoc) return;
            // The listener is still registered, so Firestore delivers the
            // doc from the local write as soon as it is applied and the
            // snapshot handler above takes over. Releasing the spinner here
            // is what stops the silence either way: if nothing is delivered
            // the account cards say so, which is a screen a person can act
            // on rather than a wait with no end.
            setLoading(false);
          } catch (e) {
            if (!current || deliveredDoc) return;
            setError(e instanceof Error ? e.message : String(e));
            setLoading(false);
          } finally {
            if (createDeadline !== undefined) window.clearTimeout(createDeadline);
          }
        })
        .catch((e) => {
          if (!current || deliveredDoc) return;
          setError(
            'The studio database is not responding on this network. ' +
              (e instanceof Error ? e.message : String(e))
          );
          setLoading(false);
        });
    }, 10000);

    return () => {
      current = false;
      window.clearTimeout(stallTimer);
      unsubscribe();
    };
  }, [user, authLoading, jrStaffDancer]);

  // Mask the previous principal on the very first render after Auth changes,
  // before effect cleanup runs. Late reads are fenced above as well.
  const ownsDocState = !authLoading && docUser === user;
  const currentUserDoc = ownsDocState ? userDoc : null;

  // Sentry.setUser(uid, role) - here, not inside the effect above, because
  // this is the moment (and the ONLY moment) the app actually has BOTH
  // halves at once: `user` (Firebase Auth) carries no role at all, and role
  // only exists on the Firestore doc `userDoc` fetched above. Every Sentry
  // issue used to show "Users: 0" no matter how many different people hit
  // it; this is what finally tells them apart.
  //
  // PRIVACY IS THE CONSTRAINT: uid and role ONLY - see setSentryUser's own
  // doc comment in lib/sentry.ts. Deliberately reads the RAW `userDoc` state
  // here, never `effectiveDoc` (computed below): `effectiveDoc` substitutes
  // a Director's "view as" preview role, and Sentry must always attribute a
  // report to the REAL signed-in person, not whatever role they happen to be
  // previewing at the moment.
  //
  // useUserDoc() mounts in well over a hundred places at once (Landing, nav,
  // chrome, assistant, feedback, every page that reads role...). Every
  // mounted instance runs this same effect and, once loaded, computes the
  // identical (uid, role) pair for the one signed-in user this tab has - so
  // the repeated calls are redundant, not conflicting, and Sentry.setUser is
  // a cheap in-memory scope write, not a network call.
  useEffect(() => {
    if (user && currentUserDoc) {
      setSentryUser(user.uid, currentUserDoc.role);
    } else {
      clearSentryUser();
    }
  }, [user, currentUserDoc]);

  // "View as" preview: an active Director can walk the app as another role,
  // on any host now, not only the demo/staging hosts (packet J3). Applied
  // here so every consumer of useUserDoc() (Landing, nav, page guards)
  // re-renders as that role at once. Gated two ways: a preview role is set,
  // and canPreviewRole says the REAL role/status may hold one. Never targets
  // 'director', so it can only ever reduce visible privilege; real access
  // stays governed server-side by the true identity.
  const previewRole = usePreviewRole(user?.uid ?? null);
  const canPreview = !!previewRole && canPreviewRole(currentUserDoc?.role, currentUserDoc?.status);
  // Memoize so the returned userDoc keeps a STABLE identity across renders.
  // Without this the spread built a fresh object every render while previewing,
  // so any consumer with userDoc in a dependency array re-ran on every render -
  // an endless re-render loop that showed up as the page flashing/reloading
  // every couple seconds in view-as preview.
  const effectiveDoc = useMemo(
    () => (canPreview && currentUserDoc ? { ...currentUserDoc, role: previewRole } : currentUserDoc),
    [canPreview, currentUserDoc, previewRole]
  );

  return {
    loading: !ownsDocState || loading,
    userDoc: effectiveDoc,
    realUserDoc: currentUserDoc,
    realRole: currentUserDoc?.role ?? null,
    previewRole: canPreview ? previewRole : null,
    error: ownsDocState ? error : null,
    reason: ownsDocState ? reason : null,
  };
}

// ---------------------------------------------------------------------------
// Director-side: list + manage every user.
//
// The Firestore rule already lets a Director read any users doc and update
// any users doc. The actions below are thin wrappers that surface the
// allowed mutations through a friendly API. Role/status are the only
// fields they touch (plus the audit fields).
//
// Until the custom-claims admin Function ships, role changes here only
// update the users doc: they do NOT grant the matching token claim that
// security rules check. The exception is 'director', which falls back to
// the verified-email bootstrap rule for Bobbie. For other staff roles
// (manager, company, ensemble, foh) we'll backfill claims in a one-time migration
// when the Function lands. Approving as 'family' is fully functional now.
// ---------------------------------------------------------------------------

export interface UseUsersListResult {
  loading: boolean;
  users: UserDoc[];
  error: string | null;
  /** True until an opted-in directory listener is confirmed by the server. */
  fromCache: boolean;
}

/**
 * The video pipeline canary's fixture guardian (docs/video-robustness-plan-
 * 2026-08-18.md section 3) carries `canary: true` and must never join a
 * people list a director, foh, or manager reads - it is a synthetic family
 * account that exists only so the canary's own upload has a real
 * authorizer to prove, not a person the studio can message or approve.
 * Mirror of isCanaryOfferingData in lib/offerings.ts.
 */
export function isCanaryUserData(value: unknown): boolean {
  return Boolean(value && typeof value === 'object' && (value as Record<string, unknown>).canary === true);
}

function sortDirectoryUsers(users: UserDoc[]): UserDoc[] {
  const roleRank = new Map<UserRole, number>(
    STAFF_ROLES.map((role, index) => [role, index])
  );
  return [...users].sort((a, b) => {
    const roleDelta =
      (roleRank.get(a.role) ?? 99) - (roleRank.get(b.role) ?? 99);
    if (roleDelta !== 0) return roleDelta;
    // email-ok: sort key only, never rendered. An account with no name on
    // it still has to land somewhere stable in the list.
    const aLabel = (a.displayName || a.email || a.uid).toLowerCase(); // email-ok: sort key
    const bLabel = (b.displayName || b.email || b.uid).toLowerCase(); // email-ok: sort key
    return aLabel.localeCompare(bLabel);
  });
}

/**
 * Subscribe to the people directory this user can read.
 * Director sees every user for approval/manage flows. Active staff see
 * active staff only, which powers teacher messaging without exposing family
 * contact info. Family users get only their own doc.
 */
/**
 * @param enabled false holds the listener closed and reports nothing rather
 *   than firing a directory read this viewer may not need or may not be
 *   allowed. Added for the Jr Staff report, whose own hook takes `enabled` and
 *   passes it to every sibling listener but could not pass it here, so the
 *   users listener opened for a Jr Staff viewer whose page renders none of it.
 *   Default true, so all sixty-odd existing callers keep the unconditional
 *   subscription they always had. Same shape and same reason as the parameter
 *   useRecentStaffAttendance gained in lib/staffAttendance.ts.
 * @param staffOnly true for a screen that needs only staff accounts (the
 *   Technique command center's teacher filter): a Manager and the Admin desk
 *   then run the same active staff query every other staff role runs,
 *   instead of every active account with every family in it. A Director
 *   keeps her bare query, which the rules prove for her and which is the one
 *   read that holds an unpromoted dancer sign-in. Default false, so every
 *   existing caller keeps exactly the read it had.
 */
export function useUsersList(trackFromCache = false, enabled = true, staffOnly = false): UseUsersListResult {
  const { userDoc, loading: meLoading } = useUserDoc();
  const [users, setUsers] = useState<UserDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);
  // A failed onSnapshot is dead for good, and the To-dos page now shows a
  // failed users read (a linked Jr Staff whose sign-ins could not be checked)
  // with Try again. This is what that button rebuilds (lib/readEpoch.ts); it
  // never moves in a healthy session, so nobody else pays a resubscribe.
  const readEpoch = useReadEpoch();

  useEffect(() => {
    if (!enabled) {
      setUsers([]);
      setLoading(false);
      setError(null);
      setFromCache(true);
      return;
    }
    if (meLoading) return;
    if (!db) {
      setLoading(false);
      setError('Firestore not initialized.');
      setFromCache(true);
      return;
    }
    if (!userDoc || userDoc.status !== 'active') {
      setUsers([]);
      setLoading(false);
      setError(null);
      setFromCache(true);
      return;
    }
    if (!isStaffRole(userDoc.role)) {
      setUsers([userDoc]);
      setLoading(false);
      setError(null);
      // This branch returns the already-loaded viewer document. It is not a
      // claim about a wider directory, so there is no listener to confirm.
      setFromCache(false);
      return;
    }

    setLoading(true);
    setError(null);
    // Only the Director may run the BARE users query: their read grant is
    // resource-independent, so Firestore can prove the unfiltered list.
    // The foh (Admin) and manager grants are `status == 'active'` per-doc, which the
    // rules prover cannot apply to an unfiltered list - the whole listener
    // was silently permission-denied and every foh people picker rendered
    // empty. Filtering on status makes the foh query provable; ordering is
    // done client-side to avoid a new composite index.
    // Every active account, families included: what a Manager and the Admin
    // desk read unless the caller asked for staff only.
    const everyActiveAccount = !staffOnly && (userDoc.role === 'foh' || userDoc.role === 'manager');
    const q =
      userDoc.role === 'director'
        ? query(collection(db, 'users'), orderBy('createdAt', 'desc'))
        : everyActiveAccount
          ? query(collection(db, 'users'), where('status', '==', 'active'))
          : query(
              collection(db, 'users'),
              where('status', '==', 'active'),
              where('role', 'in', STAFF_ROLES)
            );
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: trackFromCache },
      (snap) => {
        const docs = snap.docs
          .filter((d) => !isCanaryUserData(d.data()))
          .map((d) => d.data() as UserDoc);
        if (userDoc.role === 'director') {
          setUsers(docs);
        } else if (everyActiveAccount) {
          const createdMs = (u: UserDoc): number => {
            const c = u.createdAt as { toMillis?: () => number } | null | undefined;
            return c && typeof c.toMillis === 'function' ? c.toMillis() : 0;
          };
          setUsers([...docs].sort((a, b) => createdMs(b) - createdMs(a)));
        } else {
          setUsers(sortDirectoryUsers(docs));
        }
        setError(null);
        setFromCache(snap.metadata.fromCache);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setFromCache(true);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [enabled, meLoading, trackFromCache, userDoc, readEpoch, staffOnly]);

  return { loading, users, error, fromCache };
}

export interface UseUsersByIdsResult {
  users: UserDoc[];
  loading: boolean;
  error: string | null;
}

/**
 * ONE-SHOT, bounded read of exactly the uids asked for - never a listener,
 * never the bare collection. Packet J4 review, 2026-09-10 (TASK-QUEUE.md
 * "FIRESTORE READ BUDGET", docs/lessons.md "Firestore quota on a class
 * night"): the staff home's Tonight widget used to call useUsersList()
 * above to resolve teacher/Jr assistant names, which for a Director is the
 * BARE, LIVE users query - every document in the studio, restreaming on
 * every write, on the screen Directors open most.
 *
 * ONE getDoc PER UID (Promise.allSettled), NOT a `documentId() 'in'`
 * query. A first cut of this hook used a chunked 'in' query and reasoned
 * that it would "prove like a get()" per requested id - WRONG, per Codex's
 * PR #1086 review, and left uncorrected it silently broke live names and
 * the identity collapse for every non-Director. users/{uid}'s read rule
 * (shared/firestore.rules lines 1512-1522) authorizes a read on the
 * RESULT document's own `status` (and `role` for a Jr Staff reader), and
 * Firestore does NOT post-filter a query per document against a rule like
 * that: a `list`-shaped query (which an `in` clause is, even constrained
 * to document ids) is provable only when the rule can be checked WITHOUT
 * looking at any result's data, and this rule cannot be - so the entire
 * query is permission-denied the instant it could return even one uid the
 * viewer cannot read on the merits of that document. Only a Director's
 * unconditional isDirector() branch made the earlier version look like it
 * worked. A plain get() has no such requirement: Firestore checks it
 * against that ONE document only, so it is provable for every viewer whose
 * `status`/`role` combination the rule already lets through.
 *
 * Run once per mount on the caller's gate, re-run only when the SET of
 * requested uids changes (uidsKey), never on every render or the one-minute
 * tick. Each uid's read is independent (Promise.allSettled): a rejected
 * one (permission-denied for a uid this viewer's role cannot read, or any
 * other per-uid failure) simply does not resolve, and does not block the
 * uids that DID; the caller already has a fallback for a uid missing from
 * the result (the class's own denormalized teacherNames/assistantNames
 * snapshot), so a refused uid degrades to "no live name, no identity
 * collapse" for that ONE person rather than breaking the row or the rest
 * of the read.
 */
export function useUsersByIds(uids: readonly string[], enabled = true): UseUsersByIdsResult {
  const [users, setUsers] = useState<UserDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Stable key so the effect only re-runs when the actual SET of ids
  // changes, not on every render where the caller hands in a fresh array
  // holding the same uids (same reasoning useTonightAttendanceMarks's
  // idsKey gives, lib/attendance.ts).
  const uidsKey = [...uids].sort().join(',');

  useEffect(() => {
    if (!enabled || !db || uids.length === 0) {
      setUsers([]);
      setLoading(false);
      setError(null);
      return;
    }
    const firestore = db;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const settled = await Promise.allSettled(
        uids.map((uid) => getDoc(doc(firestore, 'users', uid)))
      );
      const results: UserDoc[] = [];
      for (const outcome of settled) {
        // A rejected promise here is normal, not exceptional - see the
        // header: a uid this viewer's role cannot read rejects on exactly
        // this call, and simply contributes nothing.
        if (outcome.status !== 'fulfilled') continue;
        const snap = outcome.value;
        if (!snap.exists()) continue;
        const data = snap.data();
        if (!data || isCanaryUserData(data)) continue;
        results.push(data as UserDoc);
      }
      if (!cancelled) {
        setUsers(results);
        setLoading(false);
      }
    })().catch((err: unknown) => {
      if (!cancelled) {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uidsKey, enabled]);

  return { users, loading, error };
}

export interface UserAppearanceDoc {
  uid: string;
  profileColor: ProfileColorName | null;
  photoURL: string | null;
}

/** Safe avatar-only directory available to every active Hub member. */
export function useUserAppearances(): {
  appearances: UserAppearanceDoc[];
  loading: boolean;
  error: string | null;
} {
  const { realUserDoc, loading: meLoading } = useUserDoc();
  const [appearances, setAppearances] = useState<UserAppearanceDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (meLoading) return;
    if (!db || realUserDoc?.status !== 'active') {
      setAppearances([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = onSnapshot(
      collection(db, 'userAppearances'),
      (snapshot) => {
        setAppearances(snapshot.docs.flatMap((appearanceDoc) => {
          const data = appearanceDoc.data() as Partial<UserAppearanceDoc>;
          if (data.uid !== appearanceDoc.id) return [];
          return [{
            uid: appearanceDoc.id,
            profileColor: isProfileColorName(data.profileColor) ? data.profileColor : null,
            photoURL: typeof data.photoURL === 'string' && data.photoURL.length <= 1000
              ? data.photoURL
              : null,
          }];
        }));
        setError(null);
        setLoading(false);
      },
      (cause) => {
        setError(cause.message);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [meLoading, realUserDoc?.status]);

  return { appearances, loading, error };
}

async function patchUser(uid: string, fields: Partial<UserDoc>): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await updateDoc(doc(db, 'users', uid), {
    ...fields,
    updatedAt: serverTimestamp(),
  });
}

/** Save the pending parent's name and untrusted dancer claim in one write. */
export function saveParentDancerClaim(
  uid: string,
  displayName: string,
  claimedDancer: ClaimedDancer
): Promise<void> {
  return patchUser(uid, { displayName, claimedDancer });
}

/**
 * pending -> active. Role defaults to 'family' (the safe default). Resolves to
 * the bag group notice, if any (see setUserRoleViaClaims).
 */
export function approveUser(
  uid: string,
  role: UserRole = 'family',
  managerTitle?: ManagerTitle | null
): Promise<string | null> {
  return setUserRoleViaClaims(uid, role, 'active', undefined, undefined, managerTitle);
}

/** pending -> rejected. Doc is kept for audit; user sees a "not approved" card. */
export async function rejectUser(uid: string): Promise<void> {
  await setUserRoleViaClaims(uid, undefined, 'rejected');
}

/** active -> inactive. User sees "account paused." */
export async function pauseUser(uid: string): Promise<void> {
  await setUserRoleViaClaims(uid, undefined, 'inactive');
}

/**
 * rejected | inactive -> active. Useful for "oops, undo" + welcome-back.
 * Resolves to the bag group notice, if any (see setUserRoleViaClaims).
 */
export function reactivateUser(
  uid: string,
  role: UserRole = 'family',
  managerTitle?: ManagerTitle | null
): Promise<string | null> {
  return setUserRoleViaClaims(uid, role, 'active', undefined, undefined, managerTitle);
}

/**
 * Change role on an already-active user (e.g. promote family -> company).
 * Pass managerTitle whenever role is 'manager' so the assignment surfaces
 * can store which flavor Kim picked; omit it (or pass null) to clear a
 * flavor. Ignored server-side unless role is 'manager'.
 */
export function setUserRole(
  uid: string,
  role: UserRole,
  managerTitle?: ManagerTitle | null
): Promise<string | null> {
  return setUserRoleViaClaims(uid, role, undefined, undefined, undefined, managerTitle);
}

/**
 * Add or remove one named-group membership through the Director-only server
 * boundary. The server transaction preserves concurrent membership edits and
 * archives stale Intern alerts when someone leaves that private group.
 */
export async function setUserGroupMembership(
  uid: string,
  group: StudioGroup,
  member: boolean
): Promise<void> {
  if (!STUDIO_GROUPS.includes(group)) throw new Error('Unknown studio group.');
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const response = await fetch('/.netlify/functions/setStudioGroup', {
    method: 'POST',
    headers: await functionHeaders(me),
    body: JSON.stringify({ uid, group, member }),
  });
  const data = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok || data.error) {
    const message = data.error === 'not_allowed'
      ? 'Only an active Director can change private page membership.'
      : data.error === 'account_not_found'
        ? 'Account not found.'
        : data.error;
    throw new Error(message || `Could not update membership (${response.status})`);
  }
}

/**
 * Set role/status as a real Firebase custom claim via the admin function. The
 * doc-based `setUserRole` already grants access through the stopgap rules; this
 * additionally sets the token claim (the long-term gate) + writes an audit
 * entry. Throws if the function isn't configured yet, so callers that want
 * "works now via the doc, hardened later via claims" should call setUserRole
 * first and treat this as best-effort.
 *
 * Resolves to the server's bag group notice, or null. An approval or role
 * change that makes a linked parent someone a bag group may hold offers her
 * to every active group carrying her dancer (bag group plan BG-3); a group
 * too full to take her says so in words, and the screen that made the change
 * shows them. The change itself is saved either way.
 */
export async function setUserRoleViaClaims(
  uid: string,
  role?: UserRole,
  status?: UserStatus,
  familyStudentId?: string,
  expectedFamilyClaim?: ClaimedDancer,
  managerTitle?: ManagerTitle | null
): Promise<string | null> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  if (!role && !status) throw new Error('No role or status change requested.');
  const res = await fetch('/.netlify/functions/setRole', {
    method: 'POST',
    headers: await functionHeaders(me),
    body: JSON.stringify({ uid, role, status, familyStudentId, expectedFamilyClaim, managerTitle }),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string; bagGroupNotice?: unknown };
  if (!res.ok || data.error) {
    const message = data.error === 'managed_dancer_account'
      ? 'This dancer login is managed by their family in Account settings.'
      : data.error === 'dancer_identity_check_failed'
        ? 'The server could not safely verify this account. Nothing changed.'
        : data.error;
    throw new Error(message || `Could not set role (${res.status})`);
  }
  if (uid === me.uid) await me.getIdToken(true);
  return typeof data.bagGroupNotice === 'string' && data.bagGroupNotice ? data.bagGroupNotice : null;
}

/**
 * Approve a pending parent and link the chosen roster record in one action.
 * Resolves to the bag group notice, if any (see setUserRoleViaClaims).
 */
export function approveParentAndLink(
  uid: string,
  familyStudentId: string,
  expectedFamilyClaim: ClaimedDancer
): Promise<string | null> {
  return setUserRoleViaClaims(uid, 'family', 'active', familyStudentId, expectedFamilyClaim);
}

/**
 * Set / clear the user's studio start date. Pass null to clear.
 * Used to power the "X years at the studio" counter on staff cards.
 */
export function setUserJoinedStudioOn(
  uid: string,
  joinedStudioOn: string | null
): Promise<void> {
  return patchUser(uid, { joinedStudioOn });
}

/** Director-side name edit, with linked teacher class copies kept in sync. */
export async function setUserDisplayName(
  uid: string,
  displayName: string | null
): Promise<void> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const idToken = await me.getIdToken();
  const response = await fetch('/.netlify/functions/setUserDisplayName', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ uid, displayName }),
  });
  const data = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok || data.error) throw new Error(data.error || 'Could not save this name.');
}

/**
 * Self-update your own display name.
 *
 * The self-update rule allows this with ONE guard: an account that already
 * holds a `teacherTitle` cannot change its own name, because a titled
 * teacher's name is copied onto every family-readable class and those copies
 * move together in the setUserDisplayName server transaction. Callers must
 * check `canSelfSetProfileName()` first; a titled account calling this gets a
 * flat PERMISSION_DENIED, which is the rule working, not a bug to route
 * around.
 *
 * Deliberately NOT the Director path: setUserDisplayName requires an active
 * Director as caller, so it cannot fix your own missing name for you.
 * The expected prior name is checked inside a Firestore transaction so a
 * Director correction that lands between screen-load and Save wins instead
 * of being silently overwritten by the stale Account form.
 */
export async function setMyDisplayName(
  uid: string,
  displayName: string,
  expectedDisplayName: string | null
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const me = getAuth().currentUser;
  if (!me || me.uid !== uid) throw new Error('Sign in required.');
  const userRef = doc(db, 'users', uid);
  await runTransaction(db, async (tx) => {
    const snapshot = await tx.get(userRef);
    if (!snapshot.exists()) throw new Error('Your account profile could not be confirmed.');
    const current = snapshot.data() as Partial<UserDoc>;
    const currentName = typeof current.displayName === 'string' && current.displayName.trim()
      ? current.displayName.trim()
      : null;
    if (currentName !== expectedDisplayName) {
      throw new Error('Your name changed somewhere else. Refresh this page and try again.');
    }
    if (Object.prototype.hasOwnProperty.call(current, 'teacherTitle')) {
      throw new Error('Ask a Director to change this name because it is linked to studio classes.');
    }
    tx.update(userRef, { displayName, updatedAt: serverTimestamp() });
  });
}

/**
 * Self-update notification prefs. Anyone signed in can set their own;
 * the standard self-update rule (no role/status changes) covers this
 * since `notifPrefs` isn't a privileged field.
 *
 * DOTTED-PATH per known key (task #31 round 4, Codex round 3 on #805, one
 * P2), deliberately NOT a whole-map patchUser({ notifPrefs: prefs })
 * replace, and deliberately NEVER writing notifPrefs.mutedIds at all.
 * Account and PushOptInGate supply only explicit edits. Landing uses its
 * transactionally guarded first-run initializer below. None of these
 * generic preference forms intentionally edits board mutes. The board
 * pages' own three-level button is
 * the only place that does, through setMyBoardNotificationLevel's
 * transaction just above. A whole-map replace here raced that path the
 * same way setMyBoardNotificationLevel's own pre-transaction bug raced
 * itself: a tab that loaded prefs before another tab's board-level change
 * would, on its own generic Save, write its stale mutedIds back and
 * silently erase the newer board setting. A transaction on THIS function
 * could not have closed that gap either - the race is between two
 * DIFFERENT write paths, not two calls to the same one - so the fix is to
 * make this path structurally incapable of touching mutedIds, rather than
 * trust every present and future caller to remember to omit it by hand.
 *
 * Firestore hands shared/firestore.rules' hasNotifPrefsShape the WHOLE
 * post-write document either way (see that function's own comment on
 * data.notifPrefs), so this dotted-path partial update validates exactly
 * as the old whole-map write did - proven in firestore-rules.test.mjs.
 *
 * Every key is optional in this patch, including the master channels.
 * An omitted key preserves the stored choice, not a copied form default.
 * This prevents an unrelated save from reversing a newer opt-in or opt-out.
 */
export async function setMyNotifPrefs(
  uid: string,
  prefs: Partial<NotifPrefs>
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  // Callers own only fields the person actually edited. A stale Account
  // form or a category-only picker must not reverse another device's choice.
  const fields: Record<string, boolean | FieldValue> = {};
  if (prefs.app !== undefined) fields['notifPrefs.app'] = prefs.app;
  if (prefs.email !== undefined) fields['notifPrefs.email'] = prefs.email;
  if (prefs.sms !== undefined) fields['notifPrefs.sms'] = prefs.sms;
  if (prefs.messages !== undefined) fields['notifPrefs.messages'] = prefs.messages;
  if (prefs.calendar !== undefined) fields['notifPrefs.calendar'] = prefs.calendar;
  if (prefs.comp !== undefined) fields['notifPrefs.comp'] = prefs.comp;
  if (prefs.digest !== undefined) fields['notifPrefs.digest'] = prefs.digest;
  if (Object.values(fields).some((value) => typeof value !== 'boolean')) {
    throw new Error('Notification choices must be on or off.');
  }
  if (Object.keys(fields).length === 0) return;
  fields.updatedAt = serverTimestamp();
  await updateDoc(doc(db, 'users', uid), fields);
}

/**
 * Finish first-run notification onboarding without replaying channel defaults
 * over a newer choice from another tab. An empty map marks an unanswered
 * profile complete while every reader keeps its documented per-key defaults.
 * Only controls explicitly edited in this picker may change existing prefs.
 */
export async function completeMyNotifPrefsOnboarding(
  uid: string,
  edits: Partial<Pick<NotifPrefs, 'app' | 'email' | 'sms'>>
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  if (getAuth().currentUser?.uid !== uid) throw new Error('Sign in required.');
  const choices: Partial<Pick<NotifPrefs, 'app' | 'email' | 'sms'>> = {};
  for (const key of ['app', 'email', 'sms'] as const) {
    const value = edits[key];
    if (value === undefined) continue;
    if (typeof value !== 'boolean') throw new Error('Notification choices must be on or off.');
    choices[key] = value;
  }
  const userRef = doc(db, 'users', uid);
  await runTransaction(db, async (tx) => {
    const snapshot = await tx.get(userRef);
    if (getAuth().currentUser?.uid !== uid) throw new Error('Sign in required.');
    if (!snapshot.exists()) throw new Error('Your account profile could not be confirmed.');
    if ((snapshot.data() as Partial<UserDoc>).notifPrefs === undefined) {
      tx.update(userRef, { notifPrefs: choices, updatedAt: serverTimestamp() });
      return;
    }
    if (Object.keys(choices).length === 0) return;
    const fields: Record<string, boolean | FieldValue> = { updatedAt: serverTimestamp() };
    for (const [key, value] of Object.entries(choices)) fields[`notifPrefs.${key}`] = value;
    tx.update(userRef, fields);
  });
}

/**
 * Self-update ONE board's three-level notifications setting (task #31).
 * A DOTTED-PATH write on notifPrefs.mutedIds only, deliberately not
 * patchUser/setMyNotifPrefs's whole-field replace: this button never reads
 * the account's channel prefs (app/email/sms/digest) before saving, so a
 * whole-field write from a stale local copy could silently revert
 * whatever those were set to a moment ago in another tab. The dotted path
 * touches only the one key; Firestore still validates the WHOLE resulting
 * document against hasNotifPrefsShape either way.
 *
 * TRANSACTIONAL (task #31 round 3, Codex round 2 on #805, finding 3): the
 * caller's `currentMutedIds` argument is only ever the value this hook
 * last rendered, which two open tabs each changing a DIFFERENT board's
 * level race on - tab A reads mutedIds = [], sets jrStaffBoard to
 * 'nothing' from its own stale []; tab B, open on a different board,
 * reads the same stale [] a moment later and sets parentBoard to
 * 'nothing' from that same stale snapshot, and its write lands last,
 * silently erasing tab A's jrStaffBoard entry even though neither tab did
 * anything wrong. arrayUnion/arrayRemove per tap was considered and
 * rejected: withBoardNotificationLevel's own per-board entries are
 * mutually exclusive ('<boardKey>' vs '<boardKey>:replies' vs neither),
 * so a single level change is really a remove-then-maybe-add PAIR
 * (leaving 'originalOnly' requires removing '<boardKey>:replies' AND
 * adding boardKey, or removing both) - two Firestore field-transform ops
 * inside ONE update, which themselves apply in an unspecified order
 * relative to each other and don't compose the way a single re-read does.
 * So instead: re-read the doc's OWN CURRENT mutedIds inside the
 * transaction, apply the level change to THAT fresh value (never the
 * stale argument), and write the corrected array back. Firestore retries
 * this transaction on a detected conflict, so two concurrent different-
 * board changes both survive - each is folded onto whatever the other one
 * just committed, not onto a snapshot from before either ran.
 *
 * The 200-entry cap (shared/firestore.rules' hasNotifPrefsShape) is still
 * enforced exactly as before: this function never bypasses document
 * validation, transactional or not, so a resulting array over the cap
 * still fails the write and throws, same as the old updateDoc did.
 */
/**
 * PACKET J30, REBUILT (Fable's refute round on PR #1148 against the first
 * version, proven by execution): a Jr Staff with two live sign-ins used to
 * get a per-account write here - her dancer doc and her email doc each held
 * their OWN mutedIds array, and a server read at delivery time guessed
 * which one was "current" off users/{uid}.updatedAt. That guess was provably
 * wrong twice over: an update on ONE sign-in raced the other's stale copy
 * back to life, and an UNRELATED updatedAt bump (a push-token enrollment,
 * a display-name edit, roughly twenty other self-writers) could make a
 * stale array look newer than a fresh one with no mute change at all. See
 * netlify/functions/lib/boardNotificationLevel.mjs's own header for the
 * full story and the fix: ONE canonical write, landing on every one of her
 * sign-ins' docs in the same server transaction.
 *
 * TWO ROUTES, chosen by MY OWN READING of "the client never calls the
 * function for an ordinary person" (the packet's own words): calling the
 * server function costs a network round trip and App Check overhead that
 * the overwhelming majority of accounts - anyone with exactly one sign-in -
 * has no reason to pay.
 *
 *   - The PLAIN local transaction (unchanged from before this packet)
 *     for everyone: the common case, and the only route when no
 *     `viewerAccount` is passed at all (every call site keeps working if a
 *     future caller forgets the new argument).
 *   - The SERVER function's `set` action, tried FIRST when `viewerAccount`
 *     - her own doc, which the caller already holds; no directory listener
 *     is added to get it - looks like one of her own sign-ins
 *     (isOwnJrStaffSignIn). This is a CHEAP, LOCAL HEURISTIC, not proof: a
 *     Jr Staff who happens to hold only one live sign-in today also passes
 *     it, and the server's own alias lookup is what actually decides. On
 *     that exact case the function answers `alias_required` (403), caught
 *     below and treated as "fall back, do not bother her with an error" -
 *     the plain local transaction still runs, so the setting still saves.
 *     Any OTHER error is real and is surfaced.
 */
export async function setMyBoardNotificationLevel(
  uid: string,
  currentMutedIds: string[] | undefined,
  boardKey: NotifiableBoardKey,
  level: BoardNotificationLevel,
  viewerAccount?: Pick<UserDoc, 'uid' | 'role' | 'status' | 'studentId' | 'authMode' | 'jrStaffPromoted'> | null,
  // A board that starts at Off for this viewer (boardStartsOffFor). No board
  // does since CX-12 (Bobbie 2026-09-29, the Jr Staff board is Everything for
  // a parent again). Only ever true on this plain path, because a Jr Staff is
  // staff on both sign-ins and never starts Off.
  levelOptions: BoardNotificationLevelOptions = {}
): Promise<void> {
  if (viewerAccount && isOwnJrStaffSignIn(viewerAccount)) {
    try {
      await requestBoardNotificationLevelSet(boardKey, level);
      return;
    } catch (error) {
      if (!(error instanceof BoardNotificationLevelRequestError) || error.code !== 'alias_required') {
        throw error;
      }
      // FALLS THROUGH to the plain local transaction below - she looked
      // like a Jr Staff sign-in but the server found no partner uid, so
      // this is an ordinary single-sign-in save after all.
    }
  }
  if (!db) throw new Error('Firestore not initialized.');
  const userRef = doc(db, 'users', uid);
  await runTransaction(db, async (tx) => {
    const snapshot = await tx.get(userRef);
    // A read failure or a genuinely missing profile both fall back to the
    // caller's own last-known value rather than inventing an empty list -
    // this is a self-write on an account that must already exist for the
    // caller to be signed in as it, so a missing doc here is the account-
    // deleted-mid-session edge, not the common path.
    const freshMutedIds = snapshot.exists()
      ? (snapshot.data() as Partial<UserDoc>).notifPrefs?.mutedIds
      : currentMutedIds;
    tx.update(userRef, {
      'notifPrefs.mutedIds': withBoardNotificationLevel(freshMutedIds, boardKey, level, levelOptions),
      updatedAt: serverTimestamp(),
    });
  });
}

/** Thrown by the two request* helpers below; `code` is the server's own error string. */
export class BoardNotificationLevelRequestError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

const BOARD_NOTIFICATION_LEVEL_ERROR_MESSAGES: Record<string, string> = {
  sign_in_required: 'Sign in required.',
  invalid_token: 'Sign in required.',
  alias_required: 'That setting could not be saved right now.',
  // A second reviewer's re-pass, 2026-09-16: the server now tells apart
  // "she genuinely has one sign-in" (alias_required, above - a silent local
  // fallback, never an error toast) from "the lookup itself failed"
  // (alias_lookup_failed, 503) - a real failure must never be treated as
  // proof she only has one sign-in, or a Firestore hiccup would quietly
  // write only her own doc and recreate the exact drift this packet exists
  // to close.
  alias_lookup_failed: 'That setting could not be saved right now. Try again in a moment.',
  bad_request: 'That setting could not be saved. Refresh and try again.',
  account_not_found: 'That setting could not be saved. Refresh and try again.',
  server_not_configured: 'The studio settings server is not configured yet.',
  'missing app check token': 'This device could not be verified. Refresh and try again.',
  'invalid app check token': 'This device could not be verified. Refresh and try again.',
  too_many_requests: 'Too many changes too quickly. Wait a bit and try again.',
};

async function postBoardNotificationLevel(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const me = getAuth().currentUser;
  if (!me) throw new BoardNotificationLevelRequestError('sign_in_required', 'Sign in required.');
  let response: Response;
  try {
    response = await fetch('/.netlify/functions/boardNotificationLevel', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: await functionHeaders(me),
      body: JSON.stringify(body),
    });
  } catch {
    throw new BoardNotificationLevelRequestError(
      'network_error', 'Could not reach the studio server. That setting was not saved.'
    );
  }
  const payload = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) {
    const code = payload.error ?? 'server_error';
    throw new BoardNotificationLevelRequestError(
      code, BOARD_NOTIFICATION_LEVEL_ERROR_MESSAGES[code] ?? 'That setting could not be saved.'
    );
  }
  return payload;
}

/**
 * The server `set` action: writes the new level to EVERY one of the
 * caller's sign-ins' docs in one transaction. See
 * netlify/functions/lib/boardNotificationLevel.mjs.
 */
async function requestBoardNotificationLevelSet(
  boardKey: NotifiableBoardKey,
  level: BoardNotificationLevel
): Promise<void> {
  await postBoardNotificationLevel({ action: 'set', boardKey, level });
}

/**
 * PER-BROWSER-TAB, NOT PER-COMPONENT-MOUNT. BoardNotificationsButton
 * renders on every board page she opens; without this a Jr Staff visiting
 * three boards in one sitting would fire three reconcile calls before any
 * of them could matter. A page reload resets it, which is fine - reconcile
 * is a no-op the instant her docs already agree, so a repeat call after a
 * reload costs one read-only transaction, never a wrong answer.
 */
const reconciledThisSession = new Set<string>();

/**
 * The server `reconcile` action: if her sign-ins' docs already agree, a
 * no-op; if they still disagree (drift from before this design shipped, or
 * from data seeded earlier), the QUIETER level per board is written to all
 * of them, once, closing it for good - see
 * netlify/functions/lib/boardNotificationLevel.mjs's computeCanonicalMutedIds
 * for why it is the quieter level, never a timestamp comparison. Called
 * once per session, silently, by BoardNotificationsButton - never surfaced
 * as an error to her, since a failed reconcile here is not a failed save,
 * only a missed chance to tidy up drift that the scheduled sweep
 * (jrStaffMuteReconcileSweep.mjs) also closes on its own clock regardless.
 *
 * alias_required IS NOT A FAILURE, and must never log as one (a second
 * reviewer's re-pass, 2026-09-16, caught this: isOwnJrStaffSignIn is a
 * cheap CLIENT heuristic, so every single-sign-in Jr Staff-shaped account
 * calls this and gets alias_required back on every session, by design -
 * logging that as an error would fill the console for the common case, not
 * the exceptional one. Any OTHER error is real and does get logged, so a
 * genuine outage is still visible.
 */
export async function reconcileMyBoardNotificationLevelOnce(uid: string): Promise<void> {
  if (reconciledThisSession.has(uid)) return;
  reconciledThisSession.add(uid);
  try {
    await postBoardNotificationLevel({ action: 'reconcile' });
  } catch (error) {
    if (error instanceof BoardNotificationLevelRequestError && error.code === 'alias_required') return;
    console.error('reconcileMyBoardNotificationLevelOnce: failed, will retry next session', error);
  }
}

/**
 * Self-update the SMS phone number (E.164). Non-privileged field, so the
 * standard self-update rule covers it. Pass null to clear.
 */
export async function setMyPhone(uid: string, phone: string | null): Promise<void> {
  return patchUser(uid, { phone });
}

async function saveMyAppearance(
  uid: string,
  fields: Pick<Partial<UserDoc>, 'profileColor' | 'photoURL'>
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const userRef = doc(db, 'users', uid);
  const appearanceRef = doc(db, 'userAppearances', uid);
  await runTransaction(db, async (tx) => {
    const snapshot = await tx.get(userRef);
    if (!snapshot.exists()) throw new Error('Your account profile could not be confirmed.');
    // Fields not being changed carry over from the APPEARANCE doc, never
    // from the users doc. The users doc's photoURL is whatever the auth
    // provider supplied at sign-in (a Google account photo, usually), and
    // userAppearances is readable by every active member - so falling back
    // to the users doc meant picking a profile COLOR quietly published a
    // person's sign-in photo they never chose to share (Codex review on
    // this PR). The appearance doc contains only what its owner explicitly
    // saved to it, which is exactly what may stay in it.
    const appearanceSnapshot = await tx.get(appearanceRef);
    const current = (appearanceSnapshot.exists()
      ? appearanceSnapshot.data()
      : {}) as Partial<UserAppearanceDoc>;
    const profileColor = Object.prototype.hasOwnProperty.call(fields, 'profileColor')
      ? fields.profileColor ?? null
      : (isProfileColorName(current.profileColor) ? current.profileColor : null);
    const photoURL = Object.prototype.hasOwnProperty.call(fields, 'photoURL')
      ? fields.photoURL ?? null
      : typeof current.photoURL === 'string' ? current.photoURL : null;
    const updatedAt = serverTimestamp();
    tx.update(userRef, { ...fields, updatedAt });
    tx.set(appearanceRef, { uid, profileColor, photoURL, updatedAt });
  });
}

/** Save the signed-in adult's own avatar color. */
export async function setMyProfileColor(
  uid: string,
  profileColor: ProfileColorName
): Promise<void> {
  const me = getAuth().currentUser;
  if (!me || me.uid !== uid) throw new Error('Sign in required.');
  return saveMyAppearance(uid, { profileColor });
}

/**
 * The Storage object path a saved profile-photo URL points at, or null for
 * anything that is not this person's own profile-photo object - a Google
 * sign-in photo URL, a legacy value, or junk. Used to find the OLD object
 * to clean up after a replace, and the current one on removal; returning
 * null simply skips the delete, so a foreign URL can never aim one.
 */
function myProfilePhotoPath(uid: string, url: string | null | undefined): string | null {
  if (typeof url !== 'string' || !url) return null;
  const marker = '/o/';
  const start = url.indexOf(marker);
  if (start < 0) return null;
  const end = url.indexOf('?', start);
  const encoded = url.slice(start + marker.length, end < 0 ? undefined : end);
  try {
    const path = decodeURIComponent(encoded);
    return path.startsWith(`profile-photos/${uid}/`) ? path : null;
  } catch {
    return null;
  }
}

/**
 * Upload and save the signed-in person's own profile photo.
 *
 * The upload goes to a fresh timestamped object, the appearance metadata
 * swaps over to it, and only then is the previous object deleted (best
 * effort). The old single fixed path overwrote the LIVE photo before any
 * metadata was saved, so a transient failure between the two destroyed a
 * working avatar while every surface still pointed at it (Codex review on
 * this PR). Now a failure before the swap deletes the new object and the
 * current photo is untouched.
 */
export async function uploadMyProfilePhoto(uid: string, file: File): Promise<string> {
  const me = getAuth().currentUser;
  if (!me || me.uid !== uid) throw new Error('Sign in required.');
  if (!storage) throw new Error('Photo storage is not available right now.');
  if (!file.type.startsWith('image/')) throw new Error('Choose a photo file.');
  if (file.size >= 8 * 1024 * 1024) throw new Error('Choose a photo smaller than 8 MB.');

  const previousUrl = (await getDoc(doc(db!, 'userAppearances', uid)))
    .data()?.photoURL as string | null | undefined;

  const ref = storageRef(storage, `profile-photos/${uid}/profile-${Date.now()}`);
  await withUploadTimeout(
    uploadBytes(ref, file, { contentType: file.type }),
    'The profile photo upload took too long. Please try again.'
  );
  const photoURL = await getDownloadURL(ref);
  try {
    await saveMyAppearance(uid, { photoURL });
  } catch (error) {
    await deleteObject(ref).catch(() => undefined);
    throw error;
  }
  const previousPath = myProfilePhotoPath(uid, previousUrl);
  if (previousPath) {
    await deleteObject(storageRef(storage, previousPath)).catch(() => undefined);
  }
  return photoURL;
}

/** Remove the signed-in person's saved profile photo. */
export async function removeMyProfilePhoto(uid: string): Promise<void> {
  const me = getAuth().currentUser;
  if (!me || me.uid !== uid) throw new Error('Sign in required.');
  if (!storage) throw new Error('Photo storage is not available right now.');
  const currentUrl = (await getDoc(doc(db!, 'userAppearances', uid)))
    .data()?.photoURL as string | null | undefined;
  // Metadata first: the moment this commits, nothing points at the object,
  // so a failed delete afterward leaks a file instead of breaking a photo.
  await saveMyAppearance(uid, { photoURL: null });
  // The pre-versioning fixed path is covered too: any object at either
  // shape parses to a path under this person's own folder.
  const path = myProfilePhotoPath(uid, currentUrl)
    ?? `profile-photos/${uid}/profile`;
  await deleteObject(storageRef(storage, path)).catch(() => undefined);
}

/**
 * Save the current teacher's chosen title and refresh every linked class's
 * family-readable teacherName copy in one server transaction.
 */
export async function setMyTeacherTitle(
  uid: string,
  teacherTitle: 'ms' | 'coach'
): Promise<{ teacherName: string; updatedOfferings: number }> {
  const me = getAuth().currentUser;
  if (!me || me.uid !== uid) throw new Error('Sign in required.');
  const idToken = await me.getIdToken();
  const response = await fetch('/.netlify/functions/setTeacherTitle', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ teacherTitle }),
  });
  const data = (await response.json().catch(() => ({}))) as {
    error?: string;
    teacherName?: string;
    updatedOfferings?: number;
  };
  if (!response.ok || data.error || !data.teacherName) {
    throw new Error(data.error || 'Could not save your teacher title.');
  }
  return {
    teacherName: data.teacherName,
    updatedOfferings: data.updatedOfferings ?? 0,
  };
}

/**
 * A DIRECTOR sets somebody else's title, and every class that person teaches
 * or assists renames with it in the same server transaction.
 *
 * This is the door DECISIONS.md's 2026-08-28 ruling asked for ("SHE ASSIGNS
 * THE TITLES HERSELF... So BUILD THE PICKER") and it is the ONLY door for a
 * Jr Staff whose sign-in is a dancer login: that session is a server cookie
 * with no Firebase Auth principal, so it can never produce the ID token
 * setMyTeacherTitle above requires. Before this existed, "Coach Parker"
 * could only come into being if Parker signed in himself, which he cannot.
 *
 * The Director check is the SERVER's, against the caller's own users doc.
 * Nothing here is a permission; the screen only decides what to offer.
 */
export async function setTeacherTitleForUser(
  uid: string,
  teacherTitle: 'ms' | 'coach'
): Promise<{ teacherName: string; updatedOfferings: number }> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const idToken = await me.getIdToken();
  const response = await fetch('/.netlify/functions/setTeacherTitle', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ teacherTitle, uid }),
  });
  const data = (await response.json().catch(() => ({}))) as {
    error?: string;
    teacherName?: string;
    updatedOfferings?: number;
  };
  if (!response.ok || data.error || !data.teacherName) {
    throw new Error(data.error || 'Could not save this title.');
  }
  return {
    teacherName: data.teacherName,
    updatedOfferings: data.updatedOfferings ?? 0,
  };
}
